// Expected provision over a settled stretch, from the provision's history.
//
// A contract only knows its current provision. When it changed during the
// period (indexation, an edited contract) "current × months" is wrong, so the
// amount per day comes from, in this order:
//   1. what Rentio actually asked the tenant for (request items, spread evenly
//      over the days each one covers);
//   2. for days Rentio did not bill: the dated indexations on record — an
//      estimate when they do not add up with the lease (it was edited at a
//      date nobody recorded), when the slice's other provision lines
//      changed, or when a step's date is a guess;
//   3. otherwise the current amount — flagged as an estimate.
// Every amount is what the tenant pays, so incl. VAT: the request items
// already are, the lease's amounts get the lease's VAT rate on top.
// The history comes from GET /settlements/provision-schedule (rentio-api-next).

import { daysInPeriod, monthsBetween } from "./engine";

// "common" settles against group costs, "private" against the unit's own.
export type ProvisionSlice = "common" | "private";

export interface ProvisionPiece {
  slice: ProvisionSlice;
  from: string;
  to: string;
  // Requested for the whole from..to stretch.
  amount: number;
  // A regular charge, or an indexation catch-up that tops up billed periods.
  kind: "regular" | "correction";
}

export interface ProvisionIndexation {
  slice: ProvisionSlice;
  // Day the new amount took effect.
  date: string;
  oldAmount: number;
  newAmount: number;
  // That day is a guess: an older indexation applied long after the lease's
  // anniversary, so the new amount may have started up to a year earlier.
  dateUncertain?: boolean;
}

// The lease field the indexations of a slice track (the common costs, or the
// non-common costs of the private slice), excl. VAT as on the lease, and the
// factor that adds the VAT the tenant pays on it.
export interface IIndexedProvisionField {
  current: number;
  // The amount the lease started with; undefined when unknown.
  start?: number;
  vatFactor: number;
  // The slice's other provision lines, which no indexation tracks, no longer
  // hold their start amounts: they changed at a date nobody recorded.
  otherLinesChanged?: boolean;
}

// How Rentio billed a lease's common or non-common costs field over a
// stretch of days (see fixedFeePeriods.utils).
export interface IProvisionPriceTypeStretch {
  field: "common" | "nonCommon";
  priceType: "provision" | "fixedFee";
  from: string;
  to: string;
}

export interface ProvisionSchedule {
  pieces: ProvisionPiece[];
  indexations: ProvisionIndexation[];
  // Absent from an API that does not send it yet.
  priceTypes?: IProvisionPriceTypeStretch[];
}

// Weakest source wins when slices or stretches differ; "none" = the contract
// carries no provision for this settlement at all; "contract" = no history,
// but the provision still equals the contract's start amount, so it never
// changed and the current amount is certain.
type TRankedProvisionSource =
  | "none"
  | "payments"
  | "contract"
  | "indexation"
  | "estimate";
// "payments_contract" = billed days next to days at that certain contract
// amount, so an average that differs from the contract's amount reads right.
export type ProvisionSource = TRankedProvisionSource | "payments_contract";
const SOURCE_RANK: Record<TRankedProvisionSource, number> = {
  none: 0,
  payments: 1,
  contract: 2,
  indexation: 3,
  estimate: 4,
};

interface Range {
  from: string;
  to: string;
}

const dayAfter = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

const dayBefore = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

const clip = (range: Range, to: Range): Range | null => {
  const from = range.from > to.from ? range.from : to.from;
  const end = range.to < to.to ? range.to : to.to;
  return from <= end ? { from, to: end } : null;
};

// Parts of `range` not covered by any of `covers`.
const uncovered = (range: Range, covers: Range[]): Range[] => {
  const sorted = covers
    .map(c => clip(c, range))
    .filter((c): c is Range => c !== null)
    .sort((a, b) => a.from.localeCompare(b.from));
  const gaps: Range[] = [];
  let cursor = range.from;
  for (const c of sorted) {
    if (c.from > cursor) gaps.push({ from: cursor, to: dayBefore(c.from) });
    if (c.to >= cursor) cursor = dayAfter(c.to);
    if (cursor > range.to) break;
  }
  if (cursor <= range.to) gaps.push({ from: cursor, to: range.to });
  return gaps;
};

// Two amounts within half a cent are the same amount.
const AMOUNT_TOLERANCE = 0.005;

const CENTS_PER_EURO = 100;

const sameAmount = (a: number, b: number): boolean =>
  Math.abs(a - b) <= AMOUNT_TOLERANCE;

const yearBefore = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

interface IStretch {
  range: Range;
  monthly: number;
  // The history does not settle this stretch's amount: the lease was edited
  // at an unknown date, or a step's day is a guess.
  uncertain: boolean;
}

// Monthly amount per stretch of `range`, from the indexation steps. Each
// stretch takes the amount known to be in force at its end: a step's old
// amount before it, the lease's current amount after the last step. With an
// intact history that is the previous step's new amount; after an edit
// between two indexations it is the amount just before the next one, and the
// stretch is uncertain because the edit's date is unknown. The steps track
// one lease field (`field`, excl. VAT); whatever else the slice holds (other
// recurring provision lines) is a constant part of every stretch, and every
// stretch is uncertain once those lines changed. Without `field` the last
// step's new amount stands for the field's current amount.
const stepsOver = (params: {
  range: Range;
  steps: ProvisionIndexation[];
  current: number;
  field?: IIndexedProvisionField;
}): IStretch[] => {
  const { range, steps, current, field } = params;
  if (steps.length === 0)
    return [{ range, monthly: current, uncertain: false }];
  const vatFactor = field?.vatFactor ?? 1;
  const withVat = (amount: number) =>
    Math.round(amount * vatFactor * CENTS_PER_EURO) / CENTS_PER_EURO;
  const last = steps[steps.length - 1];
  const indexedNow = field ? field.current : last.newAmount;
  const constant = current - withVat(indexedNow);
  const startedElsewhere =
    field?.start != null && !sameAmount(field.start, steps[0].oldAmount);
  const bounds = [
    {
      from: "0000-01-01",
      indexed: steps[0].oldAmount,
      uncertain: startedElsewhere,
    },
    ...steps.map((s, i) => {
      const next = steps[i + 1];
      return next
        ? {
            from: s.date,
            indexed: next.oldAmount,
            uncertain: !sameAmount(s.newAmount, next.oldAmount),
          }
        : {
            from: s.date,
            indexed: indexedNow,
            uncertain: !sameAmount(last.newAmount, indexedNow),
          };
    }),
  ];
  const out: IStretch[] = [];
  bounds.forEach((b, i) => {
    const nextStep = steps[i];
    const end = nextStep ? dayBefore(nextStep.date) : "9999-12-31";
    const part = clip({ from: b.from, to: end }, range);
    if (!part) return;
    // A step dated by guess may have started up to a year before its day,
    // inside this stretch.
    const guessedBoundary =
      !!nextStep?.dateUncertain &&
      clip(part, { from: yearBefore(nextStep.date), to: end }) !== null;
    out.push({
      range: part,
      monthly: Math.max(0, withVat(b.indexed) + constant),
      uncertain: b.uncertain || guessedBoundary || !!field?.otherLinesChanged,
    });
  });
  return out;
};

const daysOf = (range: Range | null): number =>
  range ? daysInPeriod(range.from, range.to) : 0;

// What a regular piece billed for days outside the lease, pro rata by day.
// One settlement reports it, so it is never returned twice: the days after
// the lease's end go with the settlement that settles its last day, the days
// before its start with the one that settles its first day.
const billedOutside = (params: {
  piece: ProvisionPiece;
  contract: Range;
  intervals: Range[];
}): number => {
  const { piece, contract, intervals } = params;
  const settles = (day: string) =>
    intervals.some(iv => iv.from <= day && day <= iv.to);
  const daysBefore = settles(contract.from)
    ? daysOf(clip(piece, { from: piece.from, to: dayBefore(contract.from) }))
    : 0;
  const daysAfter = settles(contract.to)
    ? daysOf(clip(piece, { from: dayAfter(contract.to), to: piece.to }))
    : 0;
  return (
    (piece.amount * (daysBefore + daysAfter)) /
    daysInPeriod(piece.from, piece.to)
  );
};

const shownSource = (params: {
  source: TRankedProvisionSource;
  // Some of the days came from what Rentio billed.
  billed: boolean;
}): ProvisionSource =>
  params.source === "contract" && params.billed
    ? "payments_contract"
    : params.source;

// What one slice of the provision counts: its amount and its months.
export interface ISliceProvision {
  expected: number;
  months: number;
}

export interface ExpectedProvision {
  expected: number;
  source: ProvisionSource;
  months: number;
  // Billed by Rentio for days outside the lease (see billedOutside). For
  // information only: it never counts in the expected provision.
  billedOutsideContract: number;
  // Per slice that counts, its part of `expected` and its months. The parts
  // add up to `expected` to the cent.
  bySlice: Partial<Record<ProvisionSlice, ISliceProvision>>;
}

const monthsOf = (intervals: Range[]): number =>
  intervals.reduce((sum, iv) => sum + monthsBetween(iv.from, iv.to), 0);

const roundToCents = (amount: number): number =>
  Math.round(amount * CENTS_PER_EURO) / CENTS_PER_EURO;

export function expectedProvision(params: {
  // Every day the settlement counts, of any slice.
  intervals: Range[];
  // Per slice, the days that slice counts, when they differ from
  // `intervals`: a slice whose months an earlier settlement already used
  // counts fewer of them. A slice left out counts none.
  intervalsBySlice?: Partial<Record<ProvisionSlice, Range[]>>;
  slices: ProvisionSlice[];
  // Per slice, what the tenant pays a month today, incl. VAT.
  current: Record<ProvisionSlice, number>;
  schedule?: ProvisionSchedule;
  unchanged?: Record<ProvisionSlice, boolean>;
  // Per slice, the lease field its indexations track. When given, a slice
  // without one (that field is no provision) ignores its indexations, and a
  // history that does not add up is an estimate.
  indexed?: Partial<Record<ProvisionSlice, IIndexedProvisionField>>;
  // The lease's own start and end, to report what was billed outside them.
  contract?: Range;
}): ExpectedProvision {
  const {
    intervals: allIntervals,
    intervalsBySlice,
    slices,
    current,
    schedule,
    unchanged,
    indexed,
    contract,
  } = params;
  let expected = 0;
  let outside = 0;
  const sliceExpected: Partial<Record<ProvisionSlice, number>> = {};
  const sliceMonths: Partial<Record<ProvisionSlice, number>> = {};
  let source: TRankedProvisionSource = "none";
  let billed = false;
  const worse = (s: TRankedProvisionSource) => {
    if (SOURCE_RANK[s] > SOURCE_RANK[source]) source = s;
  };

  for (const slice of slices) {
    const intervals = intervalsBySlice
      ? intervalsBySlice[slice] ?? []
      : allIntervals;
    const before = expected;
    const pieces = (schedule?.pieces ?? []).filter(p => p.slice === slice);
    const regular = pieces.filter(p => p.kind === "regular");
    const field = indexed?.[slice];
    const steps =
      indexed && !field
        ? []
        : (schedule?.indexations ?? [])
            .filter(s => s.slice === slice)
            .sort((a, b) => a.date.localeCompare(b.date));

    // A slice the contract carries no provision on — today, nor in anything
    // Rentio billed — does not count, even when its costs were indexed (a
    // flat-rate cost is indexed too) or a catch-up was billed for it.
    if (!(current[slice] > 0) && regular.length === 0) continue;

    if (contract) {
      for (const p of regular) {
        outside += billedOutside({ piece: p, contract, intervals });
      }
    }

    for (const iv of intervals) {
      for (const p of pieces) {
        const part = clip(p, iv);
        if (!part) continue;
        worse("payments");
        billed = true;
        expected +=
          (p.amount * daysInPeriod(part.from, part.to)) /
          daysInPeriod(p.from, p.to);
      }
      const gaps = uncovered(iv, regular);
      if (gaps.length === 0) continue;
      if (steps.length === 0) {
        worse(unchanged?.[slice] ? "contract" : "estimate");
      }
      for (const gap of gaps) {
        for (const seg of stepsOver({
          range: gap,
          steps,
          current: current[slice] ?? 0,
          field,
        })) {
          if (steps.length > 0) {
            worse(seg.uncertain ? "estimate" : "indexation");
          }
          expected += monthsBetween(seg.range.from, seg.range.to) * seg.monthly;
        }
      }
    }
    sliceExpected[slice] = expected - before;
    sliceMonths[slice] = monthsOf(intervals);
  }

  const total = roundToCents(expected);
  // Each slice rounded to the cent, the last one taking what rounding the
  // parts on their own gained or lost, so they always add up to the total.
  const counted = slices.filter(slice => sliceExpected[slice] != null);
  const bySlice: Partial<Record<ProvisionSlice, ISliceProvision>> = {};
  let rest = total;
  counted.forEach((slice, i) => {
    const part =
      i === counted.length - 1
        ? roundToCents(rest)
        : roundToCents(sliceExpected[slice] ?? 0);
    rest -= part;
    bySlice[slice] = { expected: part, months: sliceMonths[slice] ?? 0 };
  });
  return {
    expected: total,
    source: shownSource({ source, billed }),
    months: monthsOf(allIntervals),
    billedOutsideContract: roundToCents(outside),
    bySlice,
  };
}
